import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCouple } from '../contexts/CoupleContext';
import * as coupleService from '../services/coupleService';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Heart, Users, Calendar, Mail, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const CoupleInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { acceptInvitation, hasCouple } = useCouple();
  
  const [invitation, setInvitation] = useState(null);
  const [couple, setCouple] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [nickname, setNickname] = useState('');

  useEffect(() => {
    if (token) {
      loadInvitationDetails();
    }
  }, [token]);

  useEffect(() => {
    // Se usuário não está autenticado, redirecionar para login com redirect
    if (!isAuthenticated) {
      navigate(`/login?redirect=/couple/join/${token}`);
      return;
    }

    // Se usuário já está em um casal, mostrar erro
    if (hasCouple) {
      setError('Você já faz parte de um casal. Saia do casal atual para aceitar este convite.');
      setLoading(false);
      return;
    }
  }, [isAuthenticated, hasCouple, token, navigate]);

  const loadInvitationDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await coupleService.getInvitationDetails(token);
      setInvitation(response.invitation);
      setCouple(response.couple);
    } catch (error) {
      console.error('Erro ao carregar convite:', error);
      setError(error.response?.data?.error || 'Convite não encontrado ou expirado');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=/couple/join/${token}`);
      return;
    }

    if (hasCouple) {
      setError('Você já faz parte de um casal');
      return;
    }

    setAccepting(true);
    setError(null);

    try {
      await acceptInvitation(token, nickname);
      
      // Mostrar sucesso e redirecionar
      alert('Convite aceito com sucesso! Bem-vindo ao casal.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Erro ao aceitar convite:', error);
      setError(error.response?.data?.error || 'Erro ao aceitar convite');
    } finally {
      setAccepting(false);
    }
  };

  const handleRejectInvitation = () => {
    if (confirm('Tem certeza que deseja rejeitar este convite?')) {
      // Por enquanto apenas redireciona, futuramente podemos implementar rejeição formal
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-center space-x-2">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span>Carregando convite...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-6 w-6" />
              Convite Inválido
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">{error}</p>
            <Button onClick={() => navigate('/')} className="w-full">
              Voltar ao Início
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-6 w-6" />
              Convite de Casal
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              Você precisa estar logado para aceitar este convite.
            </p>
            <Button onClick={() => navigate(`/login?redirect=/couple/join/${token}`)} className="w-full">
              Fazer Login
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate(`/register?redirect=/couple/join/${token}`)} 
              className="w-full"
            >
              Criar Conta
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-center">
            <Heart className="h-6 w-6 text-pink-500" />
            Convite para Casal
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Informações do convite */}
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold">{couple?.name}</h2>
            <p className="text-muted-foreground">
              {invitation?.invitedBy?.name || invitation?.invitedBy?.nickname} 
              convidou você para juntar-se ao perfil de casal.
            </p>
          </div>

          {/* Detalhes do casal */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
            <div className="text-center">
              <Users className="h-8 w-8 mx-auto text-blue-500 mb-2" />
              <p className="font-medium">{couple?.memberCount}/2</p>
              <p className="text-sm text-muted-foreground">Membros</p>
            </div>
            
            {couple?.relationshipStartDate && (
              <div className="text-center">
                <Calendar className="h-8 w-8 mx-auto text-green-500 mb-2" />
                <p className="font-medium">
                  {new Date(couple.relationshipStartDate).toLocaleDateString('pt-BR')}
                </p>
                <p className="text-sm text-muted-foreground">Início</p>
              </div>
            )}
            
            <div className="text-center">
              <Mail className="h-8 w-8 mx-auto text-purple-500 mb-2" />
              <p className="font-medium">
                {new Date(invitation?.invitedAt).toLocaleDateString('pt-BR')}
              </p>
              <p className="text-sm text-muted-foreground">Convite enviado</p>
            </div>
          </div>

          {/* Informações do usuário */}
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Você será adicionado como: <strong>{user?.email}</strong>
            </p>
          </div>

          {/* Formulário de aceite */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="nickname">Como gostaria de ser chamado? (opcional)</Label>
              <Input
                id="nickname"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder={user?.name || user?.email?.split('@')[0] || 'Seu apelido'}
                maxLength={50}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Este será o nome exibido para seu parceiro no casal.
              </p>
            </div>

            {error && (
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-destructive text-sm">{error}</p>
              </div>
            )}

            <div className="flex gap-4">
              <Button 
                onClick={handleAcceptInvitation} 
                disabled={accepting}
                className="flex-1"
              >
                {accepting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Aceitando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Aceitar Convite
                  </>
                )}
              </Button>
              
              <Button 
                variant="outline" 
                onClick={handleRejectInvitation}
                disabled={accepting}
                className="flex-1"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Rejeitar
              </Button>
            </div>
          </div>

          {/* Informações sobre o que acontece ao aceitar */}
          <div className="text-xs text-muted-foreground space-y-1 p-4 bg-blue-50 rounded-lg border">
            <p className="font-medium text-blue-800">O que acontece ao aceitar:</p>
            <ul className="list-disc list-inside text-blue-700 space-y-1">
              <li>Você se torna membro do casal "{couple?.name}"</li>
              <li>Poderá ver e colaborar em conflitos compartilhados</li>
              <li>Terá acesso às estatísticas do relacionamento</li>
              <li>Pode sair do casal a qualquer momento</li>
            </ul>
          </div>

          {/* Data de expiração */}
          {invitation?.expiresAt && (
            <p className="text-xs text-center text-muted-foreground">
              Este convite expira em {new Date(invitation.expiresAt).toLocaleString('pt-BR')}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CoupleInvite;